import { Injectable } from '@angular/core';
import { ApiHttpClient, FormRequest, ListOption, ListOptionRequest, SearchBaseResponse } from '../modules/tcm-core.module';
import { CampaignCandidateListOptionsRequest, DuplicatePatientCheckRequest, MarketingCandidateListOptionsRequest, PatientDetailsForAppointmentViewModel, PatientDetailsForSalesOrderViewModel, PatientFormResponse, PatientOptionWithClinicAndProvider, PatientViewModel, UpdatePatientPhotoRequest, PatientValidationResultViewModel, PatientSelectSearchRowViewModel } from '../models/patients';
import { SmsMessageModel } from '../models/external-services-info';
import { SendPatientSmsRequest } from '../models/patients/send-patient-sms-request';
import { TcmHttpClient } from '../internals/tcm-http-client';

@Injectable({
  providedIn: 'root'
})
export class PatientDataService {

  constructor(private readonly httpClient: ApiHttpClient, private tcmHttpClient: TcmHttpClient) {
  }

  public getPatientDetailsAsync(id?: number): Promise<PatientFormResponse> {
    const request: FormRequest = { id: id };
    return this.httpClient.getJsonAsync<PatientFormResponse>('Patients/Details?request=' + encodeURIComponent(JSON.stringify(request)));
  }

  public getPatientDetailsForSalesOrderAsync(patientId: number, clinicId: number, cancellationToken: Promise<any>): Promise<PatientDetailsForSalesOrderViewModel> {
    const request = { patientId: patientId, clinicId: clinicId };
    return this.httpClient.getJsonAsync<PatientDetailsForSalesOrderViewModel>('Patients/DetailsForSalesOrder', request, cancellationToken);
  }

  public getPatientDetailsForAppointmentAsync(patientId: number, clinicId: number, appointmentId: number, referralSourceId:number, cancellationToken: Promise<any>): Promise<PatientDetailsForAppointmentViewModel> {
    const request = { patientId: patientId, clinicId: clinicId, appointmentId: appointmentId, referralSourceId: referralSourceId };
    return this.httpClient.getJsonAsync<PatientDetailsForAppointmentViewModel>('Patients/DetailsForAppointment', request, cancellationToken);
  }

  public getPatientOptionsAsync(query: string, cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Patients/Options', request, cancellationToken);
  }

  public getPatientOptionsWithClinicAndProviderAsync(query: string, cancellationToken: Promise<any>): Promise<PatientOptionWithClinicAndProvider[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<PatientOptionWithClinicAndProvider[]>('Patients/OptionsWithClinicAndProvider', request, cancellationToken);
  }

  public getMarketingCandidateListOptionsAsync(marketingListId: number, query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: MarketingCandidateListOptionsRequest = {
      marketingListId: marketingListId,
      searchString: query
    };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Patients/GetMarketingCandidateListOptions', request, cancellationToken);
  }

  public getCampaignCandidateListOptionsAsync(campaignId: number, query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: CampaignCandidateListOptionsRequest = {
      campaignId: campaignId,
      searchString: query
    };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Patients/GetCampaignCandidateListOptions', request, cancellationToken);
  }

  public updatePatientPhotoAsync(request: UpdatePatientPhotoRequest): Promise<any> {
    return this.httpClient.postJsonAsync('PatientWidget/updatePatientPhoto', request);
  }

  public anonymizeAsync(patientId: number, cancellationToken: Promise<any>): Promise<any> {
    //var request = JSON.parse(JSON.stringify(viewModel));

    return this.httpClient.postJsonAsync('Patients/Anonymize', patientId, cancellationToken);
  }

  public checkPatientDuplicateAsync(request: DuplicatePatientCheckRequest, cancellationToken: Promise<any>): Promise<number> {
    return this.httpClient.getJsonAsync('Patients/CheckPatientDuplicate', request, cancellationToken);
  }

  public replacePatientAsync(viewModel: PatientViewModel, duplicatePatientId: number, cancellationToken: Promise<any>): Promise<any> {
    var request = JSON.parse(JSON.stringify(viewModel));

    request.id = duplicatePatientId;

    return this.httpClient.postJsonAsync('Patients/ReplacePatient', request, cancellationToken);
  }

  public sendSmsAsync(patientId: number, phoneNumber: string, smsText: string, userStickyClinicId: number, timeout?: Promise<any>): Promise<boolean> {
    var data = { phoneNumber, smsText } as SmsMessageModel;
    const request: SendPatientSmsRequest = { patientId, data, userStickyClinicId };
    return this.httpClient.postJsonAsync('Patients/SendSms', request, timeout);
  }

  public checkPatientNumberExistsAsync(patientNumber: any, patientId?: number, timeout?: Promise<any>): Promise<boolean> {
    const request = { patientNumber: patientNumber, patientId: patientId };

    return this.httpClient.getJsonAsync<boolean>('Patients/CheckPatientNumberExists', request, timeout);
  }

  public checkPatientErpIdExistsAsync(erpId: any, patientId?: number, timeout?: Promise<any>): Promise<boolean> {
    const request = { erpId: erpId, patientId: patientId };

    return this.httpClient.getJsonAsync<boolean>('Patients/checkPatientErpIdExist', request, timeout);
  }

  public validatePatientInfoAsync(patientId: number, clinicId?:number, insuranceId?: number, appointmentTypeId?: number, cancellationToken: Promise<any> = null): Promise<PatientValidationResultViewModel> {
    const request = { patientId: patientId, clinicId: clinicId, insuranceId: insuranceId, appointmentTypeId };
    return this.httpClient.getJsonAsync<PatientValidationResultViewModel>('Patients/ValidatePatientInfo', request, cancellationToken);
  }

  public validatePatientInfoBySalesOrderAsync(salesOrderId: number, cancellationToken: Promise<any> = null): Promise<PatientValidationResultViewModel> {
    const request = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync<PatientValidationResultViewModel>('Patients/ValidatePatientInfoBySalesOrder', request, cancellationToken);
  }

  public validatePatientInfoByAppointmentAsync(appointmentId: number, cancellationToken: Promise<any> = null): Promise<PatientValidationResultViewModel> {
    const request = { appointmentId: appointmentId };
    return this.httpClient.getJsonAsync<PatientValidationResultViewModel>('Patients/ValidatePatientInfoByAppointment', request, cancellationToken);
  }

  getSelectPatientPotencialCountAsync(firstName: string, lastName: string, homePhone: string, cellPhone: string, email: string, cancellationToken: Promise<any>): Promise<SearchBaseResponse<PatientSelectSearchRowViewModel>> {
    var requestData = JSON.stringify({
      paging: {},
      sorting: {},
      filters: {
        firstName: firstName,
        lastName: lastName,
        homePhone: homePhone,
        cellPhone: cellPhone,
        email: email
      },
      requestTotal: true
    })

    return this.httpClient.getJsonAsync<SearchBaseResponse<PatientSelectSearchRowViewModel>>('Patients/SelectSearch', { request: requestData }, cancellationToken)
  }

  canCreateAppointment(patientId: number, timeout?: Promise<any>): Promise<boolean> {
    const request = { patientId: patientId };
    return this.httpClient.getJsonAsync<boolean>('Patients/CanCreateAppointment', request, timeout);
  }

  exportNoahSessions(patientId: number, actionIds: number[], password: string, timeout?: Promise<any>) {
    return this.tcmHttpClient.getFileAsyncWithPost('NoahExport/ExportFile', { patientId: patientId, actionsId: actionIds, filePassword: password });
  }
  updateLatest(rightEquipment: number,leftEquipment: number,patientId: number, cancellation: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('PatientWidget/UpdateLatest', { patientId: patientId, rightEquipment: rightEquipment, leftEquipment: leftEquipment }, cancellation);
  }
}
